import { useState, useRef, useEffect, useCallback } from 'react'
import { API_BASE as API } from '../../config'
import { authHeaders } from '../utils/auth'
import { speak as ttsSpeak, stopTTS } from '../utils/tts'
import { useNavigate, useParams } from 'react-router-dom'
import { getDay } from '../data/curriculum'
import { getRolePlayTopic } from '../data/roleplay_topics'
import { useProgress } from '../hooks/useProgress'
import { readSSEStream } from '../utils/stream'
import '../EL.css'

const EL = '/english-learning'

/* ── Non-streaming AI call for structured data ── */
async function aiAsk(message, systemPrompt, signal) {
  const res = await fetch(`${API}/english-tutor/stream`, {
    method: 'POST', headers: authHeaders(), signal,
    body: JSON.stringify({ message, history: [], subject_info: systemPrompt })
  })
  if (!res.ok) throw new Error(res.status)
  return readSSEStream(res.body.getReader())
}

/* ── TTS helpers — delegated to shared tts.js ── */
function speakText(text, onEnd) {
  ttsSpeak(text, 'en-US', undefined, onEnd)
}

/* ── AI reaction messages ── */
function getReaction(score, streak) {
  if (score >= 90) return { msg: 'وااو! إجابة ممتازة جداً! 🎉', color: '#10b981', emoji: '🌟' }
  if (score >= 70) return { msg: 'أحسنت! واضح إنك فاهم الموضوع 💪', color: '#3b82f6', emoji: '✅' }
  if (score >= 50) return { msg: 'كويس! بس في مجال للتحسين — جرب مرة ثانية 🔁', color: '#f59e0b', emoji: '💡' }
  return { msg: 'لا تستسلم! كل محاولة أفضل من السابقة 🤗', color: '#ef4444', emoji: '💪' }
}

export default function ELRolePlayPage({ darkMode, setDarkMode }) {
  const { levelId, dayId } = useParams()
  const navigate = useNavigate()
  const progress = useProgress()
  const day = getDay(levelId, Number(dayId))
  const topic = getRolePlayTopic(Number(dayId))

  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [ttsEnabled, setTtsEnabled] = useState(true)
  const [ttsPlaying, setTtsPlaying] = useState(false)
  const [phase, setPhase] = useState('intro') // intro | chat | review
  const [roundCount, setRoundCount] = useState(0)
  const [lastReaction, setLastReaction] = useState(null)
  const [showTips, setShowTips] = useState(false)
  const [recording, setRecording] = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const chunksRef = useRef([])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => () => clearTimeout(reviewTimerRef.current), [])

  const startRolePlay = () => {
    const aiOpener = topic.starterLine
    setMessages([{ role: 'ai', content: aiOpener, ttsText: aiOpener }])
    setPhase('chat')
    if (ttsEnabled) {
      setTtsPlaying(true)
      speakText(aiOpener, () => setTtsPlaying(false))
    }
  }

  // XP is earned by actually completing an exchange, not by opening the
  // screen — awarding it on start let a user farm unlimited XP by clicking
  // Start then immediately ending/retrying in a loop with zero real practice.
  const finishRolePlay = () => {
    stopTTS()
    if (roundCount > 0) progress.addXP?.('debateRound')
    setPhase('review')
  }

  const rpAbortRef = useRef(null)
  const reviewTimerRef = useRef(null)
  useEffect(() => () => rpAbortRef.current?.abort(), [])

  const send = useCallback(async () => {
    const text = input.trim()
    if (!text || loading) return
    setInput('')
    stopTTS()
    setTtsPlaying(false)

    rpAbortRef.current?.abort()
    rpAbortRef.current = new AbortController()
    const signal = rpAbortRef.current.signal

    const userMsg = { role: 'user', content: text }
    const newMessages = [...messages, userMsg]
    setMessages([...newMessages, { role: 'ai', content: '', typing: true }])
    setLoading(true)
    setRoundCount(r => r + 1)

    // Step 1: natural in-character streaming reply
    const roleplaySys = `You are ${topic.aiRole}. ${topic.aiPersonality}. Setting: ${topic.setting}.
Stay completely in character. Keep your response to 1-2 sentences. Encourage use of: ${topic.focusWords.join(', ')}.`

    const rpHeaders = authHeaders()

    try {
      const res = await fetch(`${API}/english-tutor/stream`, {
        method: 'POST',
        headers: rpHeaders,
        signal,
        body: JSON.stringify({
          message: text,
          history: newMessages.slice(-6).map(m => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.content })),
          subject_info: roleplaySys
        })
      })

      if (!res.ok) throw new Error('server')

      const full = await readSSEStream(res.body.getReader(), (_, accumulated) => {
        setMessages(prev => {
          const copy = [...prev]
          copy[copy.length - 1] = { role: 'ai', content: accumulated, typing: false }
          return copy
        })
      })

      const replyText = full.trim()

      // H-5: fire TTS and grammar correction in parallel
      if (ttsEnabled && replyText) {
        setTtsPlaying(true)
        speakText(replyText, () => setTtsPlaying(false))
      }

      // Step 2: grammar correction (runs while TTS plays)
      let correction = null
      try {
        const corrPrompt = `"${text}" ← هذه الجملة كتبها طالب إنجليزي.
ابحث عن خطأ واحد فقط (إملاء أو قواعد).
أجب بهذا الشكل الحرفي فقط بدون أي نص إضافي:
WRONG: [الكلمة أو العبارة الخاطئة من الجملة، أو: none]
RIGHT: [النسخة الصحيحة، أو: none]
WHY: [جملة عربية قصيرة تشرح السبب]`
        const corrRaw = await aiAsk(corrPrompt, 'أنت مصحح لغوي. أجب فقط بالسطور الثلاثة المطلوبة.', signal)
        const wM = corrRaw.match(/WRONG:\s*(.+)/i)
        const rM = corrRaw.match(/RIGHT:\s*(.+)/i)
        const yM = corrRaw.match(/WHY:\s*(.+)/i)
        if (wM && wM[1].trim().toLowerCase() !== 'none' && wM[1].trim() !== '') {
          correction = {
            error: wM[1].trim().replace(/["""]/g, ''),
            fix: rM?.[1].trim().replace(/["""]/g, '') || '',
            note: yM?.[1].trim() || ''
          }
        } else if (corrRaw.trim() && !corrRaw.toLowerCase().includes('none') && corrRaw.length < 250) {
          const lines = corrRaw.split('\n').map(l => l.trim()).filter(Boolean)
          const noteLine = lines.find(l => /[؀-ۿ]/.test(l))
          const engLines = lines.filter(l => !/[؀-ۿ]/.test(l) && l.length < 60)
          if (engLines.length >= 2) {
            correction = { error: engLines[0], fix: engLines[1], note: noteLine || '' }
          } else if (noteLine) {
            correction = { error: '', fix: '', note: noteLine }
          }
        }
      } catch { /* correction is optional */ }

      setMessages(prev => {
        const copy = [...prev]
        copy[copy.length - 1] = { role: 'ai', content: replyText, correction, typing: false }
        return copy
      })

      // Calculate rough reaction score based on input length and vocabulary use
      const usedFocusWords = topic.focusWords.filter(w => text.toLowerCase().includes(w.toLowerCase())).length
      const score = Math.min(100, 40 + text.split(' ').length * 3 + usedFocusWords * 15)
      setLastReaction(getReaction(score, roundCount))

      if (roundCount >= 7) {
        clearTimeout(reviewTimerRef.current)
        reviewTimerRef.current = setTimeout(finishRolePlay, 2000)
      }

    } catch (e) {
      if (e.name !== 'AbortError') {
        setMessages(prev => {
          const copy = [...prev]
          copy[copy.length - 1] = { role: 'ai', content: '⚠️ خطأ في الاتصال — تأكد من الإنترنت وحاول مرة ثانية.', typing: false }
          return copy
        })
      }
    }
    setLoading(false)
    inputRef.current?.focus()
  }, [input, messages, loading, topic, levelId, dayId, day, ttsEnabled, roundCount])

  const toggleVoiceRecord = async () => {
    if (recording) { mediaRecorderRef.current?.stop(); return }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      chunksRef.current = []
      const mr = new MediaRecorder(stream)
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop())
        setRecording(false)
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        setTranscribing(true)
        try {
          const form = new FormData()
          form.append('audio', blob, 'recording.webm')
          const res = await fetch(`${API}/english-tutor/transcribe`, { method: 'POST', body: form })
          const d = await res.json()
          if (res.ok && d.transcript && d.transcript !== '[inaudible]') {
            setInput(d.transcript)
          } else {
            // Recording worked but nothing usable came back — say so instead
            // of leaving the input silently empty, which looks identical to
            // the mic never having worked at all.
            alert('ما سمعنا كلام واضح 🎤 — جرّبي تحكي أقرب للميكروفون أو اكتبي إجابتك')
          }
        } catch {
          alert('صار خطأ بالاتصال أثناء تحويل الصوت لنص — جرّبي مرة ثانية أو اكتبي إجابتك')
        }
        setTranscribing(false)
      }
      mediaRecorderRef.current = mr
      setRecording(true)
      mr.start()
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        alert('لم يُمنح إذن الميكروفون — اسمحي للموقع باستخدام الميكروفون من إعدادات المتصفح/الجهاز 🎤')
      } else if (err?.name === 'NotFoundError') {
        alert('ما لقينا ميكروفون على هذا الجهاز')
      } else {
        alert('تعذّر تشغيل التسجيل — جرّبي مرة ثانية')
      }
    }
  }

  if (!day) return <div className={`el-app${darkMode ? ' el-dark' : ''}`}><div className="el-page"><p style={{ padding: 32 }}>Not found.</p></div></div>

  return (
    <div className={`el-app${darkMode ? ' el-dark' : ''}`}>
      <div className="el-page el-rp-page">

        {/* Header */}
        <header className="el-top-bar">
          <button className="el-icon-btn" onClick={() => { stopTTS(); navigate(`${EL}/level/${levelId}/day/${dayId}`) }}>←</button>
          <div className="el-rp-header-center">
            <span className="el-rp-icon">{topic.icon}</span>
            <div>
              <div className="el-rp-title">{topic.scenario}</div>
              <div className="el-rp-subtitle">{topic.scenarioAr}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className={'el-icon-btn' + (ttsEnabled ? ' active' : '')}
              onClick={() => { if (ttsEnabled) stopTTS(); setTtsEnabled(e => !e) }}
              title={ttsEnabled ? 'أوقف صوت AI' : 'شغّل صوت AI'}
            >
              {ttsPlaying ? '🔊' : ttsEnabled ? '🔈' : '🔇'}
            </button>
          </div>
        </header>

        {/* Intro Phase */}
        {phase === 'intro' && (
          <div className="el-rp-intro">
            <div className="el-rp-scene-card">
              <div className="el-rp-scene-icon">{topic.icon}</div>
              <h2 className="el-rp-scene-title">{topic.scenario}</h2>
              <p className="el-rp-scene-setting">{topic.setting}</p>
              <div className="el-rp-scene-ar">{topic.settingAr}</div>

              <div className="el-rp-role-info">
                <div className="el-rp-role-you">
                  <span className="el-rp-role-label">أنت</span>
                  <span className="el-rp-role-desc">تلعب دورك الطبيعي</span>
                </div>
                <div className="el-rp-role-ai">
                  <span className="el-rp-role-label">AI</span>
                  <span className="el-rp-role-desc">{topic.aiRole}</span>
                  <span className="el-rp-role-ar">{topic.aiRoleAr}</span>
                </div>
              </div>

              <div className="el-rp-focus-words">
                <div className="el-rp-focus-label">🎯 كلمات الهدف اليوم:</div>
                <div className="el-rp-focus-chips">
                  {topic.focusWords.map(w => <span key={w} className="el-rp-word-chip">{w}</span>)}
                </div>
              </div>

              <div className="el-rp-starter-preview">
                <div className="el-rp-starter-label">سيبدأ {topic.aiRole} بقول:</div>
                <div className="el-rp-starter-text">"{topic.starterLineAr}"</div>
              </div>

              <button className="el-nav-btn primary el-rp-start-btn" onClick={startRolePlay}>
                🎭 ابدأ التمثيل اللغوي
              </button>

              <button className="el-rp-tips-toggle" onClick={() => setShowTips(s => !s)}>
                💡 {showTips ? 'إخفاء' : 'عرض'} النصائح
              </button>
              {showTips && (
                <div className="el-rp-tips">
                  {topic.tips.map((t, i) => <div key={i} className="el-rp-tip">• {t}</div>)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chat Phase */}
        {phase === 'chat' && (
          <>
            <div className="el-rp-focus-bar">
              {topic.focusWords.map(w => <span key={w} className="el-rp-word-chip mini">{w}</span>)}
              <span className="el-rp-round-count">{roundCount}/8 جولات</span>
            </div>

            <div className="el-rp-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`el-rp-msg-row ${msg.role}`}>
                  {msg.role === 'ai' && (
                    <div className="el-rp-avatar">{topic.icon}</div>
                  )}
                  <div className={`el-rp-bubble ${msg.role}`}>
                    {msg.role === 'ai' && <div className="el-rp-bubble-name">{topic.aiRole}</div>}
                    {msg.typing ? (
                      <span className="el-typing-dots"><span /><span /><span /></span>
                    ) : (
                      <div className="el-rp-bubble-text">{msg.content}</div>
                    )}
                    {msg.role === 'ai' && msg.content && !msg.typing && (
                      <button
                        className="el-rp-tts-btn"
                        onClick={() => {
                          if (ttsPlaying) { stopTTS(); setTtsPlaying(false) }
                          else { setTtsPlaying(true); speakText(msg.content, () => setTtsPlaying(false)) }
                        }}
                      >
                        {ttsPlaying ? '⏹' : '🔊'}
                      </button>
                    )}
                    {msg.role === 'ai' && msg.correction && (
                      <div className="el-rp-correction-wrap" style={{ marginTop: 8 }}>
                        {msg.correction.error && <div className="el-rp-correction-error">❌ {msg.correction.error}</div>}
                        {msg.correction.fix && <div className="el-rp-correction-fix">✅ {msg.correction.fix}</div>}
                        {msg.correction.note && <div className="el-rp-correction-note">💡 {msg.correction.note}</div>}
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && <div className="el-rp-avatar user">👤</div>}
                </div>
              ))}

              {lastReaction && (
                <div className="el-rp-reaction" style={{ borderColor: lastReaction.color }}>
                  <span>{lastReaction.emoji}</span> {lastReaction.msg}
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="el-rp-input-row">
              <input
                ref={inputRef}
                className="el-rp-input"
                placeholder={transcribing ? 'جاري تحويل صوتك لنص...' : 'ردّ على الشخصية بالإنجليزية...'}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
                disabled={loading || transcribing}
              />
              <button
                className={'el-rp-stop-btn' + (recording ? ' recording' : '')}
                onClick={toggleVoiceRecord}
                disabled={loading || transcribing}
                title={recording ? 'أوقف التسجيل' : 'سجّلي ردّك بصوتك'}
              >
                {recording ? '⏹' : '🎤'}
              </button>
              <button
                className="el-rp-stop-btn"
                onClick={() => { stopTTS(); setTtsPlaying(false) }}
                title="أوقف الصوت"
              >
                ⏹
              </button>
              <button className="el-rp-send-btn" onClick={send} disabled={loading || transcribing || !input.trim()}>
                {loading ? '⏳' : '↑'}
              </button>
            </div>

            <div className="el-rp-end-row">
              <button className="el-rp-end-btn" onClick={finishRolePlay}>
                إنهاء الجلسة والمراجعة →
              </button>
            </div>
          </>
        )}

        {/* Review Phase */}
        {phase === 'review' && (
          <div className="el-rp-review">
            <div className="el-rp-review-card">
              <div className="el-rp-review-icon">🎭</div>
              <h2>انتهت الجلسة!</h2>
              <div className="el-rp-review-stats">
                <div className="el-rp-stat">
                  <div className="el-rp-stat-num">{roundCount}</div>
                  <div className="el-rp-stat-label">جولات</div>
                </div>
                <div className="el-rp-stat">
                  <div className="el-rp-stat-num">{topic.focusWords.filter(w => messages.some(m => m.role === 'user' && m.content.toLowerCase().includes(w.toLowerCase()))).length}/{topic.focusWords.length}</div>
                  <div className="el-rp-stat-label">كلمات مستخدمة</div>
                </div>
              </div>

              <div className="el-rp-used-words">
                <div className="el-rp-used-label">الكلمات التي استخدمتها:</div>
                <div className="el-rp-focus-chips">
                  {topic.focusWords.map(w => {
                    const used = messages.some(m => m.role === 'user' && m.content.toLowerCase().includes(w.toLowerCase()))
                    return <span key={w} className={`el-rp-word-chip ${used ? 'used' : 'missed'}`}>{used ? '✓' : '✗'} {w}</span>
                  })}
                </div>
              </div>

              <div className="el-rp-review-tips">
                <div className="el-rp-used-label">نصائح للمرة القادمة:</div>
                {topic.tips.map((t, i) => <div key={i} className="el-rp-tip">• {t}</div>)}
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button className="el-nav-btn" onClick={() => { setMessages([]); setRoundCount(0); setLastReaction(null); setPhase('intro') }}>
                  🔄 مرة ثانية
                </button>
                <button className="el-nav-btn primary" onClick={() => navigate(`${EL}/level/${levelId}/day/${dayId}`)}>
                  ← العودة لليوم
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
